<template>
  <div v-show="selectedStudy && currentTab" class="AnalysisFiles">
		<div class="d-sr-only-focusable float-right text-decoration-underline" tabindex="0" @keyup.enter="focusToTop" @click="focusToTop" ref="firstFocusable" role="link">Go to top of page</div>
    <h2 class="left-align">Download Analysis Files</h2>
		<br/>
    <h3 class="p1 left-align download-step-header">
      <b>Step 1.</b> Select the data to be included in the analysis files.
    </h3>
		<p class="text-subtitle-1 p1 left-align font-italic">
      Please note if you have a study with a large number of respondents or recalls, consider using the date range option to download your data in parts.
    </p>
		<p class="text-subtitle-1 p1 left-align font-italic second-download-subtitle">
      If you need help with a batch download, please contact us <a @click="$router.push('/contact')" @keyup.enter="$router.push('/contact')" tabindex="0" :aria-label="'Here: If you need help with a batch download, please contact us here or at asa24helpdesk@westat.com'" style="font-weight: bold; text-decoration: underline;">here</a> or at <strong>asa24helpdesk@westat.com</strong>.
    </p>
    <v-row>
			<v-col cols="12" md="6">
				<fieldset class="border-0">
					<legend class="d-sr-only">
						Data to be included in the analysis files
					</legend>
					<v-radio-group v-model="dataDownloadSelected">
						<v-radio  label="All respondents (batch)" value="0"></v-radio>
						<v-radio label="One respondent based on username (e.g. Organic151)" value="1"></v-radio>
						<v-radio label="Range of respondent IDs between start ID and end ID" value="2"></v-radio>
						<v-radio label="Recall/Record dates between start date and end date" value="3"></v-radio>
					</v-radio-group>
				</fieldset>
			</v-col>
      <v-col cols="12" md="6">
        <div v-if="dataDownloadSelected" class="download-analysis-extra-fields">
          <div v-if="dataDownloadSelected === '1'">
            <v-form ref="form1" v-model="valid1" lazy-validation>
              <v-text-field
                v-model="username"
                hide-details="auto"
                outlined
                label="Username"
                :rules="requiredRule"
                :error="inputHasError"
                :error-messages="inputHasError ? errorMessage : []"
              ></v-text-field>
            </v-form>
          </div>
          <div v-else-if="dataDownloadSelected === '2'">
            <v-form ref="form2" v-model="valid2" lazy-validation>
              <v-text-field
                v-model="startId"
                hide-details="auto"
                outlined
                type="number"
								min="0"
                label="Start ID"
                :rules="requiredRule"
                :error="inputHasError"
                :error-messages="inputHasError ? errorMessage : []"
              ></v-text-field>
              <br />
              <v-text-field
                v-model="endId"
                hide-details="auto"
                outlined
                type="number"
								min="0"
                label="End ID"
                :rules="requiredRule"
                :error="inputHasError"
                :error-messages="inputHasError ? errorMessage : []"
              ></v-text-field>
            </v-form>
          </div>
          <div v-else-if="dataDownloadSelected === '3'">
            <v-form ref="form3" v-model="valid3" lazy-validation>
							<v-menu  v-model="startDateMenu" :close-on-content-click="false" offset-y tile allow-overflow>
								<template v-slot:activator="{ on, attrs }">
								<v-text-field
								v-model="startDate"
								hide-details="auto"
								outlined
								label="Start Date"
								:rules="startDateRules"
								type="date"
								:error="inputHasError"
                :error-messages="inputHasError ? errorMessage : []"
								v-bind="attrs"
								v-on="on"
								@keydown.tab='startDateMenu = false'
								:aria-label="'Start Date: ' + startDate"
							>
							</v-text-field>
								</template>
								<v-date-picker v-model="startDate" color="#42729B" style="margin: 0px; float: left" @input="startDateMenu = false"></v-date-picker>
							</v-menu>
              <br />
							<v-menu  v-model="endDateMenu" :close-on-content-click="false" offset-y tile allow-overflow>
  							<template v-slot:activator="{ on, attrs }">
									<v-text-field
										v-model="endDate"
										hide-details="auto"
										outlined
										:rules="endDateRules"
										type="date"
										label="End Date"
										:error="inputHasError"
										:error-messages="inputHasError ? errorMessage : []"
										v-bind="attrs"
										v-on="on"
										@keydown.tab='endDateMenu = false'
										:aria-label="'End Date: ' + endDate"
										>
										</v-text-field>
								</template>
								<v-date-picker v-model="endDate" color="#42729B" style="margin: 0px; float: left" @input="endDateMenu = false"></v-date-picker>
							</v-menu>
            </v-form>
          </div>
        </div>
      </v-col>
    </v-row>
		<v-btn
			text
			class="btn-default download-analysis-button"
			@click="onDownloadFiles()"
			:loading="isDownloadingAnalysis"
			:disabled="!dataDownloadSelected"
		>
			Download Analysis Files
		</v-btn>
    <h3 class="p1 left-align download-step-header">
      <b>Step 2.</b> Select <b>Download Data Dictionaries</b> to obtain detail information
      about the files or <b> Download Analysis Files</b> to obtain your data.
    </h3>
    <p class="text-subtitle-1 p1 left-align font-italic">
      The information will be downloaded in zip format. A free version of the
      7zip software can be found at the
      <a href="https://www.7-zip.org/download.html" target="_blank" aria-label="7Zip Web site - New Window"
        >7Zip Web site</a
      >. The 7zip file contains the below file types in CSV format.
    </p>
		<p class="text-subtitle-1 p1 left-align font-italic second-download-subtitle">
			For more information select the 'DOWNLOAD DATA DICTIONARIES' button.
		</p>
    <ul class="p1 left-align download-ul">
      <li class="dictionary-li">
        <b>RESPONSES:</b> Responses Analysis File - Food and supplement names
        from the Quick List, probe questions and answers
      </li>
      <li class="dictionary-li">
        <b>ITEMS:</b> Individual Foods Analysis File – FNDDS food codes, gram
        weights, nutrients, and Food Pattern Equivalents for each food reported
      </li>
      <li class="dictionary-li">
        <b>TOTALS:</b> Daily Total Analysis File – FNDDS nutrients and Food
        Pattern Equivalents for all foods in a given day
      </li>
      <li class="dictionary-li">
        <b>INS:</b> Individual Supplements Analysis File – Supplement Codes with
        their nutrients – keeping only those nutrients which are related to the
        FNDDS
      </li>
      <li class="dictionary-li">
        <b>TS:</b> Daily Total Supplements Analysis File – total nutrients from
        all supplements reported in a given day – keeping only those ingredients
        which are related to the FNDDS
      </li>
      <li class="dictionary-li">
        <b>TNS:</b> Daily Total Nutrients from Foods and Supplements Analysis
        File – FNDDS nutrients from all foods and supplements reported in a
        given day
      </li>
    </ul>
    <v-btn
      text
      class="btn-default download-dictionary-button"
      @click="onDownloadDictionaries()"
    >
      Download Data Dictionaries
    </v-btn>
  </div>
</template>

<script>
// import { researcherApiService } from '@/services/researcherApiService.js';
import { analysisApiService } from '@/services/analysisApiService.js';
import { utilityService } from '@/services/utilityService.js';
export default {
	name: 'AnalysisFiles',
	// props: {},
	data: () => ({
		dataDownloadSelected: null,
		username: '',
		startId: null,
		endId: null,
		startDate: null,
		endDate: null,
		studyUuid: '',
		requiredRule: [(v) => (v && v !== '') || 'This section is required'],
		startDateRules: [(v) => (v && v !== '') || 'Improper date or missing value(s)'],
		endDateRules: [(v) => (v && v !== '') || 'Improper date or missing value(s)'],
		endIdRules: [(v) => (v && v !== '') || 'This section is required'],
		inputHasError: false,
		errorMessage: '',
		valid1: true,
		valid2: true,
		valid3: true,
		startDateMenu: false,
		endDateMenu: false,
		isDownloadingAnalysis: false
	}),
	mounted () {
		this.updateSelected();
		this.endDateRules.push(
			(v) => v >= this.startDate || 'Date must be greater than or equal to the start date'
		);
		this.endIdRules.push(
			(v) => v > this.startId || 'End ID must be greater than the start ID'
		);

		this.$nextTick(function () {
			var v_radio_group = document.getElementsByClassName('v-input--radio-group__input');
			for (var i = 0; i < v_radio_group.length; i++) {
				v_radio_group[i].removeAttribute('aria-labelledby');
			}
		});
	},
	watch: {
		dataDownloadSelected: function (newVal, oldVal) {
			this.resetFields();
			if (oldVal === '1') {
				this.$refs.form1.resetValidation();
			} else if (oldVal === '2') {
				this.$refs.form2.resetValidation();
			} else if (oldVal === '3') {
				this.$refs.form3.resetValidation();
			}
		}
	},
	methods: {
		onDownloadFiles () {
			const me = this;
			const errorMessage = 'There was an error downloading the files';
			var valid = false;

			switch (this.dataDownloadSelected) {
			case '0':
				this.isDownloadingAnalysis = true;
				analysisApiService
					.downloadAllAnalysisFiles(
						this.selectedStudy.studyId,
						this.selectedStudy.studyTool,
						this.selectedStudy.version
					)
					.then(function (response) {
						if (response) {
							me.downloadZipFile(response);
						}
						me.isDownloadingAnalysis = false;
					}).catch(function (error) {
						me.isDownloadingAnalysis = false;
						me.$store.dispatch('changeUpdateStatusWithMessage', { status: 'generic-error', message: errorMessage });
					});
				break;
			case '1':
				valid = this.$refs.form1.validate();
				if (valid) {
					this.isDownloadingAnalysis = true;
					analysisApiService
						.downloadUserAnalysisFiles(
							this.selectedStudy.studyId,
							this.selectedStudy.studyTool,
							this.selectedStudy.version,
							this.username
						)
						.then(function (response) {
							if (response) {
								me.downloadZipFile(response);
							}
							me.isDownloadingAnalysis = false;
						})
						.catch(function (e) {
							me.isDownloadingAnalysis = false;
							me.$store.dispatch('changeUpdateStatusWithMessage', { status: 'generic-error', message: errorMessage });
						});
				}
				break;
			case '2':
				valid = this.$refs.form2.validate();
				if (valid) {
					this.isDownloadingAnalysis = true;
					analysisApiService
						.downloadIdRangeAnalysisFiles(
							this.selectedStudy.studyId,
							this.selectedStudy.studyTool,
							this.selectedStudy.version,
							this.startId,
							this.endId
						)
						.then(function (response) {
							if (response) {
								me.downloadZipFile(response);
							}
							me.isDownloadingAnalysis = false;
						})
						.catch(function (e) {
							me.isDownloadingAnalysis = false;
							me.$store.dispatch('changeUpdateStatusWithMessage', { status: 'generic-error', message: errorMessage });
						});
				}
				break;
			case '3':
				valid = this.$refs.form3.validate();
				if (valid) {
					this.isDownloadingAnalysis = true;
					var unixStartDate = new Date(this.startDate);
					var unixEndDate = new Date(this.endDate);
					// one day added to endDate so the range fills to the end of the day the user is sellecting
					unixEndDate.setDate(unixEndDate.getDate() + 1)
					analysisApiService
						.downloadDateRangeAnalysisFiles(
							this.selectedStudy.studyId,
							this.selectedStudy.studyTool,
							this.selectedStudy.version,
							unixStartDate / 1000,
							unixEndDate / 1000
						)
						.then(function (response) {
							if (response) {
								me.downloadZipFile(response);
							}
							me.isDownloadingAnalysis = false;
						})
						.catch(function (e) {
							me.isDownloadingAnalysis = false;
							me.$store.dispatch('changeUpdateStatusWithMessage', { status: 'generic-error', message: errorMessage });
						});
				}
			}
		},
		onDownloadDictionaries () {
			window.open('https://epi.grants.cancer.gov/asa24/researcher/sample.html', '_blank');
		},
		updateSelected () {
			this.studyUuid = this.$store.getters.getSelectedStudy.studyUuid;
			this.resetFields();
			this.dataDownloadSelected = null;
		},
		resetFields () {
			this.username = '';
			this.startId = null;
			this.endId = null;
			this.startDate = null;
			this.endDate = null;
		},
		downloadZipFile (response) {
			const url = window.URL.createObjectURL(new Blob([response.data]));
			const link = document.createElement('a');
			link.href = url;

			var currentDate = new Date();
			var date = currentDate.getFullYear() + '_';
			date += currentDate.getMonth() + 1 + '_';
			date += currentDate.getDate() + '_';
			date += currentDate.getHours() + '_';
			date += currentDate.getMinutes() + '_';
			date += currentDate.getSeconds();

			link.setAttribute(
				'download',
				`${this.selectedStudy.studyId}_${date}.zip`
			);
			document.body.appendChild(link);
			link.click();
		},
		/**
		 * Checks if the current menu tab is this component. If it is then set the focus to the first item.
		 */
		async checkTabMatch ({ tabIndex, subTabIndex } = {}) {
			const me = this
			const tabsPos = await utilityService.getTabsPositionByName('Download Analysis Files', this.$studyTabConfig);
			if (tabsPos.tabPos === tabIndex && tabsPos.subtabPos === subTabIndex) {
				var myFirstElement = me.$refs.firstFocusable
				setTimeout(() => {
					myFirstElement.focus();
				}, '350');
				// this.$refs.firstFocusable.$children[0].$el.children[0].children[1].focus();
			}
		},
		focusToTop () {
			var myHeader = document.getElementById('skipToMain')
			myHeader.focus()
		}
	},
	computed: {
		selectedStudy: function () {
			// If the selectedStudy changes, the radio buttons and checkbox properties need to be updated.
			if (this.studyUuid !== this.$store.getters.getSelectedStudy.studyUuid) {
				this.updateSelected();
			}
			return this.$store.getters.getSelectedStudy;
		},
		currentTab: function () {
			const tabPosition = {
				tabIndex: parseInt(this.$store.getters.getCurrentStudyTabIndex),
				subTabIndex: parseInt(this.$store.getters.getCurrentStudySubTabIndex)
			};

			this.checkTabMatch(tabPosition);

			return tabPosition;
		}
	}
};
</script>

<style>
</style>
